import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { Check } from 'lucide-react'
import { capability, DFY_BUNDLES, CONSULTING } from '@gjl/voice/pricing'

// Prices render from @gjl/voice/pricing. Edit them there.
const donorMgmt = capability('donor-management')!
const concierge = DFY_BUNDLES.find((b) => b.id === 'concierge')!
const intensive = CONSULTING.find((c) => c.id === 'strategy-intensive')!

const PROBLEMS = [
  {
    problem: 'Platform fees eat the gift',
    description:
      'Most giving tools take 3 to 4 percent of every donation, on top of card fees. On a year of giving that is a real number your mission never sees.',
  },
  {
    problem: 'Receipts are written by hand',
    description:
      'Every January someone spends a week assembling tax letters from bank statements and memory. Donors wait, and some ask twice.',
  },
  {
    problem: 'Nobody knows who lapsed',
    description:
      'The donor who gave three years running and then stopped is your most recoverable gift, and the one no spreadsheet ever surfaces.',
  },
  {
    problem: 'Donors and audience live apart',
    description: 'Your giving tool does not know who bought tickets or opens your emails. Every appeal goes out blind.',
  },
]

const FAQS = [
  {
    q: 'Is accepting donations really free?',
    a: 'Yes, forever. A Donate button with money going straight to your own Stripe account, 0% platform fee. You only ever pay Stripe card processing. Nobody should pay software a cut to accept a gift.',
  },
  {
    q: `What does the $${donorMgmt.priceMonthly}/mo buy, then?`,
    a: 'The management layer: recurring giving, automated tax receipts, campaign tracking, and donor records (who gave what and when, segments, lapsed-donor lists). You pay to stop losing track of donors, not to accept money.',
  },
  {
    q: 'Who runs it?',
    a: `Today our team sets up and runs donor management for you; self-serve controls are rolling out. If you want fundraising itself run end to end, that lives in Concierge (${concierge.price}), because good fundraising is strategy plus execution, not a task queue. The ${intensive.name} (${intensive.price}) is the usual starting point.`,
  },
  {
    q: 'Can we bring our donor history?',
    a: 'Yes. Simple imports are part of setup; large donor-history migrations are scoped separately. Your data is always exportable, free, on the way out.',
  },
]

export default function DonorManagementPage() {
  const breadcrumbs = generateBreadcrumbs('/services/donor-management', breadcrumbLabels)

  useSEO({
    title: 'Donor Management for Music Organizations',
    description:
      'Accept donations free (0% platform fee). Add recurring giving, automated tax receipts, and donor records for $25/mo, or have Fugue Lab run your fundraising.',
    url: '/services/donor-management',
    canonical: '/services/donor-management',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Donor Management for Music Organizations',
      description:
        'Free donation acceptance with 0% platform fee, plus donor management for music organizations, with fundraising run end to end in Concierge engagements.',
      url: '/services/donor-management',
    }),
  })

  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const problemsRef = useStaggeredGrid<HTMLDivElement>('.problem-card', { stagger: 0.08 })
  const pricingRef = useStaggeredGrid<HTMLDivElement>('.pricing-card', { stagger: 0.12 })
  const faqRef = useScrollReveal<HTMLDivElement>('.faq-item', { stagger: 0.08 })

  return (
    <div className="mx-auto max-w-6xl space-y-24 px-4 py-16">
      {/* Hero */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <h1 className="text-fluid-h1">Keep the whole gift. Keep the donor.</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">
          Accepting donations on Fugue Lab is free, with a 0% platform fee: the money goes straight to your own Stripe
          account. Donor management is the layer that makes giving grow.
        </p>
        <p className="text-fluid-body text-muted mx-auto max-w-2xl">
          Recurring gifts, automated tax receipts, campaigns, and a donor record that actually knows your audience, for
          ${donorMgmt.priceMonthly} a month. Or have us run fundraising end to end.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call →
              </Button>
            </motion.div>
          </a>
          <Link to="/packages">
            <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
              See All Pricing
            </Button>
          </Link>
        </div>
      </section>

      {/* Problems */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">What giving tools get wrong</h2>
        <div ref={problemsRef} className="grid gap-6 md:grid-cols-2">
          {PROBLEMS.map((item) => (
            <motion.div
              key={item.problem}
              className="problem-card border-border space-y-2 rounded-lg border p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{item.problem}</h3>
              <p className="text-muted-foreground text-sm">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Unified CRM differentiator */}
      <section className="border-primary/30 bg-primary/5 mx-auto max-w-4xl space-y-4 rounded-2xl border p-8 text-center">
        <h2 className="text-fluid-h2">Your donors already sit in your audience</h2>
        <p className="text-muted-foreground mx-auto max-w-2xl">
          Because giving runs on the same platform as your email and ticketing, every donor is one record with the rest
          of their history: the concerts they attended, the newsletters they open. Ask for the right gift from the right
          person, and see who loves you but has never been asked.
        </p>
        <p className="text-muted-foreground mx-auto max-w-2xl text-sm">
          A standalone giving tool cannot see any of that. Pair this page with{' '}
          <Link to="/services/email-marketing" className="text-primary underline">
            Email Marketing
          </Link>{' '}
          and the appeal writes its own segment.
        </p>
      </section>

      {/* Pricing */}
      <section className="space-y-8">
        <h2 className="text-fluid-h2 text-center">Free to accept. Priced to manage.</h2>
        <div ref={pricingRef} className="mx-auto grid max-w-6xl gap-8 md:grid-cols-3">
          <motion.div
            className="pricing-card border-border rounded-2xl border p-8"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <h3 className="mb-2 text-2xl font-bold">Accept Donations</h3>
            <div className="mb-4 flex items-baseline gap-1">
              <span className="text-4xl font-bold">Free</span>
              <span className="text-muted-foreground">forever</span>
            </div>
            <ul className="mb-8 space-y-3">
              {[
                'Donate button on your site',
                'Money straight to your own Stripe',
                '0% platform fee',
                'Only Stripe card processing applies',
              ].map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">{f}</span>
                </li>
              ))}
            </ul>
            <Link to="/platform/features/donations" className="block">
              <Button variant="outline" className="w-full cursor-pointer">
                How It Works
              </Button>
            </Link>
          </motion.div>
          <motion.div
            className="pricing-card border-primary/50 bg-primary/5 rounded-2xl border p-8"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div className="text-primary mb-2 text-sm font-medium">The Management Layer</div>
            <h3 className="mb-2 text-2xl font-bold">{donorMgmt.name}</h3>
            <div className="mb-4 flex items-baseline gap-1">
              <span className="text-4xl font-bold">${donorMgmt.priceMonthly}</span>
              <span className="text-muted-foreground">/month</span>
            </div>
            <ul className="mb-8 space-y-3">
              {[
                'Recurring giving',
                'Automated tax receipts',
                'Campaign tracking',
                'Donor records, segments, lapsed lists',
                'Set up and run for you by our team today',
              ].map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">{f}</span>
                </li>
              ))}
            </ul>
            <Link to="/get-started" className="block">
              <Button className="w-full cursor-pointer">Get Started</Button>
            </Link>
          </motion.div>
          <motion.div
            className="pricing-card border-border rounded-2xl border p-8"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div className="text-primary mb-2 text-sm font-medium">Done For You</div>
            <h3 className="mb-2 text-2xl font-bold">Fundraising, run for you</h3>
            <div className="mb-4 flex items-baseline gap-1">
              <span className="text-3xl font-bold">{concierge.name}</span>
            </div>
            <p className="text-primary mb-4 text-lg font-bold">{concierge.price}</p>
            <ul className="mb-8 space-y-3">
              {[
                'Campaign strategy and appeal copy',
                'Segments and lapsed-donor outreach',
                'Receipting oversight',
                'Part of everything-done-for-you, because fundraising is strategy plus execution',
              ].map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">{f}</span>
                </li>
              ))}
            </ul>
            <Link to="/services/done-for-you#concierge" className="block">
              <Button variant="outline" className="w-full cursor-pointer">
                Learn More
              </Button>
            </Link>
          </motion.div>
        </div>
        <p className="text-muted-foreground text-center text-sm">Pay annually on any plan and get 2 months free.</p>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Questions</h2>
        <div ref={faqRef} className="space-y-6">
          {FAQS.map((f) => (
            <div key={f.q} className="faq-item border-border rounded-lg border p-6">
              <h3 className="text-foreground mb-2 font-semibold">{f.q}</h3>
              <p className="text-muted-foreground text-sm">{f.a}</p>
            </div>
          ))}
        </div>
        <div className="pt-4 text-center">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="cursor-pointer">
              Book a Free Call →
            </Button>
          </a>
        </div>
      </section>
    </div>
  )
}
